import React, { useEffect, useRef, useState } from 'react';
import './OrbitRun.css';
import { useAuth } from '../contexts/AuthContext';
import wizardSprite from '../img/spritesheet.png';

interface OrbitRunProps {
  onOpenLoginSignup?: () => void;
}

interface DetailSpot {
  dx: number; // offset from center, as a fraction of radius
  dy: number;
  r: number;  // size, as a fraction of radius
}

interface Obstacle {
  x: number;
  y: number;
  width: number;
  height: number;
  type: 'planet' | 'satellite' | 'asteroid';
  // Visual style, decided once at spawn so details don't flicker frame-to-frame.
  // Flying ('planet'): 0 ringed planet, 1 cratered moon, 2 gas giant, 3 red planet, 4 UFO
  // Ground ('asteroid'): 0 asteroid rock spire, 1 crystal shards, 2 crashed satellite
  variant: number;
  spots: DetailSpot[]; // crater / patch positions (moon, red planet)
  jitter: number[];    // per-obstacle random offsets for rock chunks
}

interface Star {
  x: number;
  y: number;
  size: number;
  speed: number;
}

interface Confetti {
  x: number;
  y: number;
  vx: number;
  vy: number;
  color: string;
  size: number;
  shape: 'square' | 'circle';
}

interface LeaderboardEntry {
  handle: string;
  score: number;
  timestamp: number;
}

const OrbitRun: React.FC<OrbitRunProps> = ({ onOpenLoginSignup }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [gameState, setGameState] = useState<'idle' | 'playing' | 'gameover'>('idle');
  const [score, setScore] = useState(0);
  const [highScore, setHighScore] = useState(0);
  const [leaderboard, setLeaderboard] = useState<LeaderboardEntry[]>([]);
  const [savingScore, setSavingScore] = useState(false);
  const { currentUser, userHandle } = useAuth();
  const [canvasSize, setCanvasSize] = useState({ width: 600, height: 300 });
  const [isMobile, setIsMobile] = useState(typeof window !== 'undefined' && window.innerWidth < 768);
  const [showCelebration, setShowCelebration] = useState(false);
  const [madeTop10, setMadeTop10] = useState(false);

  // Game state refs
  const gameStateRef = useRef<'idle' | 'playing' | 'gameover'>('idle');
  const spriteRef = useRef<HTMLImageElement | null>(null);
  const playerRef = useRef({
    x: 80,
    y: 240 - 48, // Start on ground (groundY - PLAYER_HEIGHT), will be updated on first render
    velocityY: 0,
    isJumping: false,
    frameIndex: 0,
    animationCounter: 0
  });
  const obstaclesRef = useRef<Obstacle[]>([]);
  const starsRef = useRef<Star[]>([]);
  const confettiRef = useRef<Confetti[]>([]);
  const celebrationStartTimeRef = useRef<number>(0);
  const scoreRef = useRef(0);
  const gameSpeedRef = useRef(5);
  const frameCountRef = useRef(0);
  const animationFrameRef = useRef<number | null>(null);
  const canvasWidthRef = useRef(600);
  const canvasHeightRef = useRef(300);
  const groundYRef = useRef(240);

  // Use responsive canvas size
  const CANVAS_WIDTH = canvasSize.width;
  const CANVAS_HEIGHT = canvasSize.height;
  const GROUND_Y = CANVAS_HEIGHT * 0.8; // 80% down the canvas

  // Update refs when canvas size changes
  useEffect(() => {
    canvasWidthRef.current = CANVAS_WIDTH;
    canvasHeightRef.current = CANVAS_HEIGHT;
    groundYRef.current = GROUND_Y;

    // Update player position to be on the ground when canvas size changes
    if (gameStateRef.current === 'idle' || gameStateRef.current === 'gameover') {
      playerRef.current.y = GROUND_Y - PLAYER_HEIGHT;
    }
  }, [CANVAS_WIDTH, CANVAS_HEIGHT, GROUND_Y]);
  const GRAVITY = 0.6;
  const JUMP_STRENGTH = -12;
  const PLAYER_WIDTH = 48;
  const PLAYER_HEIGHT = 48;
  const SPRITE_WIDTH = 64;
  const SPRITE_HEIGHT = 64;

  // Update canvas size to fill entire window
  useEffect(() => {
    const updateCanvasSize = () => {
      const mobile = window.innerWidth < 768;
      setIsMobile(mobile);

      // Get the actual container size (OrbitRun window)
      const container = document.querySelector('.orbit-run');
      if (container) {
        const rect = container.getBoundingClientRect();
        setCanvasSize({ width: rect.width, height: rect.height });
      } else {
        // Fallback: use window inner dimensions
        setCanvasSize({ width: window.innerWidth, height: window.innerHeight });
      }
    };

    updateCanvasSize();
    window.addEventListener('resize', updateCanvasSize);
    return () => window.removeEventListener('resize', updateCanvasSize);
  }, []);

  // Sprite animation frames - 64x64 per frame
  // Idle animation (row 25, 2 frames)
  const IDLE_FRAMES = [
    { x: 0, y: 1600 },    // row 25, frame 0 (25 * 64 = 1600)
    { x: 64, y: 1600 }    // row 25, frame 1
  ];

  // Run cycle (row 41, frames 0-7)
  const RUN_FRAMES = [
    { x: 0, y: 2624 },    // row 41, frame 0 (41 * 64 = 2624)
    { x: 64, y: 2624 },   // frame 1
    { x: 128, y: 2624 },  // frame 2
    { x: 192, y: 2624 },  // frame 3
    { x: 256, y: 2624 },  // frame 4
    { x: 320, y: 2624 },  // frame 5
    { x: 384, y: 2624 },  // frame 6
    { x: 448, y: 2624 }   // frame 7
  ];

  // Jump animation (row 29, 4 frames)
  const JUMP_FRAMES = [
    { x: 0, y: 1856 },    // row 29, frame 0 (29 * 64 = 1856)
    { x: 64, y: 1856 },   // frame 1
    { x: 128, y: 1856 },  // frame 2
    { x: 192, y: 1856 }   // frame 3
  ];

  // Initialize stars
  useEffect(() => {
    const stars: Star[] = [];
    for (let i = 0; i < 30; i++) {
      stars.push({
        x: Math.random() * canvasWidthRef.current,
        y: Math.random() * (groundYRef.current - 20), // Only above ground
        size: Math.random() * 2 + 1,
        speed: Math.random() * 2 + 1
      });
    }
    starsRef.current = stars;
  }, [canvasSize]);

  // Load sprite
  useEffect(() => {
    const img = new Image();
    img.src = wizardSprite;
    img.onload = () => {
      spriteRef.current = img;
    };
  }, []);

  // Fetch leaderboard
  const fetchLeaderboard = async () => {
    try {
      const response = await fetch('https://us-central1-moodboards-b8265.cloudfunctions.net/app/getLeaderboard');
      const data = await response.json();
      if (data.success) {
        setLeaderboard(data.leaderboard);
      }
    } catch (error) {
      console.error('Error fetching leaderboard:', error);
    }
  };

  // Save score
  const saveScore = async () => {
    if (!currentUser || !userHandle || savingScore) return;

    setSavingScore(true);
    try {
      const token = await currentUser.getIdToken();
      const response = await fetch('https://us-central1-moodboards-b8265.cloudfunctions.net/app/saveScore', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          handle: userHandle,
          score: scoreRef.current
        })
      });

      const data = await response.json();
      if (data.success) {
        await fetchLeaderboard();
      }
    } catch (error) {
      console.error('Error saving score:', error);
    } finally {
      setSavingScore(false);
    }
  };

  // Initialize obstacles
  const createObstacle = (): Obstacle => {
    const types: ('planet' | 'satellite' | 'asteroid')[] = ['planet', 'asteroid'];
    const type = types[Math.floor(Math.random() * types.length)];
    const groundY = groundYRef.current;

    let width, height, y;
    let variant: number;
    const spots: DetailSpot[] = [];
    const jitter: number[] = [Math.random(), Math.random(), Math.random(), Math.random()];

    if (type === 'planet') {
      // Flying obstacle
      width = 40;
      height = 40;
      y = groundY - height - 50 - Math.random() * 40;
      variant = Math.floor(Math.random() * 5);

      if (variant === 1) {
        // Cratered moon: pick crater spots once so they don't flicker
        const craterCount = 3 + Math.floor(Math.random() * 2);
        for (let i = 0; i < craterCount; i++) {
          const angle = Math.random() * Math.PI * 2;
          const dist = Math.random() * 0.55;
          spots.push({
            dx: Math.cos(angle) * dist,
            dy: Math.sin(angle) * dist,
            r: 0.12 + Math.random() * 0.12
          });
        }
      } else if (variant === 3) {
        // Red planet: darker terrain patches
        for (let i = 0; i < 3; i++) {
          const angle = Math.random() * Math.PI * 2;
          const dist = Math.random() * 0.5;
          spots.push({
            dx: Math.cos(angle) * dist,
            dy: Math.sin(angle) * dist,
            r: 0.14 + Math.random() * 0.14
          });
        }
      }
    } else {
      // Ground hazard
      width = 20;
      height = 40 + Math.random() * 20;
      y = groundY - height;
      variant = Math.floor(Math.random() * 3);
    }

    return {
      x: canvasWidthRef.current,
      y,
      width,
      height,
      type,
      variant,
      spots,
      jitter
    };
  };

  // Draw functions
  const drawBackground = (ctx: CanvasRenderingContext2D) => {
    // Black space background
    ctx.fillStyle = '#000000';
    ctx.fillRect(0, 0, canvasWidthRef.current, canvasHeightRef.current);

    // Draw stars only above ground
    ctx.fillStyle = '#ffffff';
    starsRef.current.forEach(star => {
      if (star.y < groundYRef.current - 10) {
        ctx.fillRect(star.x, star.y, star.size, star.size);
      }
    });

    // Draw ground line
    ctx.strokeStyle = '#ffffff';
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(0, groundYRef.current);
    ctx.lineTo(canvasWidthRef.current, groundYRef.current);
    ctx.stroke();
  };

  const drawPlayer = (ctx: CanvasRenderingContext2D) => {
    const player = playerRef.current;

    if (spriteRef.current && spriteRef.current.complete) {
      // Draw sprite if loaded
      let frame;

      if (gameStateRef.current === 'idle') {
        // Animate idle with 2 frames
        const idleFrameIndex = Math.floor(player.animationCounter / 10) % IDLE_FRAMES.length;
        frame = IDLE_FRAMES[idleFrameIndex];
      } else if (player.isJumping) {
        // Use jump frame based on vertical position
        const jumpProgress = Math.min(3, Math.floor((player.y - (groundYRef.current - PLAYER_HEIGHT)) / -30));
        const jumpFrameIndex = Math.max(0, Math.min(3, jumpProgress));
        frame = JUMP_FRAMES[jumpFrameIndex];
      } else {
        frame = RUN_FRAMES[player.frameIndex];
      }

      ctx.drawImage(
        spriteRef.current,
        frame.x, frame.y,
        SPRITE_WIDTH, SPRITE_HEIGHT,
        player.x, player.y,
        PLAYER_WIDTH, PLAYER_HEIGHT
      );
    } else {
      // Fallback: draw simple white rectangle if sprite not loaded
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(player.x, player.y, PLAYER_WIDTH, PLAYER_HEIGHT);
    }
  };

  // Cheap retro terminator: clip to the planet body and fill an offset circle
  // so a crescent on the lower-right reads as the night side.
  const shadePlanet = (
    ctx: CanvasRenderingContext2D,
    cx: number,
    cy: number,
    r: number,
    color: string
  ) => {
    ctx.save();
    ctx.beginPath();
    ctx.arc(cx, cy, r, 0, Math.PI * 2);
    ctx.clip();
    ctx.fillStyle = color;
    ctx.beginPath();
    ctx.arc(cx + r * 0.65, cy + r * 0.5, r, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();
  };

  const drawFlyingObstacle = (ctx: CanvasRenderingContext2D, obstacle: Obstacle) => {
    const cx = Math.floor(obstacle.x + obstacle.width / 2);
    const cy = Math.floor(obstacle.y + obstacle.height / 2);
    const half = obstacle.width / 2;
    const frame = frameCountRef.current;

    switch (obstacle.variant) {
      case 0: {
        // Ringed planet (Saturn-like)
        const r = Math.floor(half * 0.8);
        const ringRx = Math.floor(half) + 2;
        const ringRy = Math.max(5, Math.floor(r * 0.5));
        const tilt = -0.35;

        // Back half of ring (behind the planet)
        ctx.strokeStyle = '#c8c0a0';
        ctx.lineWidth = 3;
        ctx.beginPath();
        ctx.ellipse(cx, cy, ringRx, ringRy, tilt, Math.PI, Math.PI * 2);
        ctx.stroke();

        // Body with darker bands
        ctx.fillStyle = '#e0b050';
        ctx.beginPath();
        ctx.arc(cx, cy, r, 0, Math.PI * 2);
        ctx.fill();
        ctx.save();
        ctx.beginPath();
        ctx.arc(cx, cy, r, 0, Math.PI * 2);
        ctx.clip();
        ctx.fillStyle = '#b8863a';
        ctx.fillRect(cx - r, cy - Math.floor(r * 0.45), r * 2, 3);
        ctx.fillRect(cx - r, cy + Math.floor(r * 0.25), r * 2, 2);
        ctx.restore();
        shadePlanet(ctx, cx, cy, r, '#8a6428');

        // Front half of ring (over the planet)
        ctx.strokeStyle = '#f0ead2';
        ctx.beginPath();
        ctx.ellipse(cx, cy, ringRx, ringRy, tilt, 0, Math.PI);
        ctx.stroke();
        break;
      }
      case 1: {
        // Cratered moon
        const r = Math.floor(half) - 2;
        ctx.fillStyle = '#c8ccd8';
        ctx.beginPath();
        ctx.arc(cx, cy, r, 0, Math.PI * 2);
        ctx.fill();
        ctx.fillStyle = '#8e93a6';
        for (const spot of obstacle.spots) {
          ctx.beginPath();
          ctx.arc(cx + spot.dx * r, cy + spot.dy * r, Math.max(1.5, spot.r * r), 0, Math.PI * 2);
          ctx.fill();
        }
        shadePlanet(ctx, cx, cy, r, '#6a6f82');
        break;
      }
      case 2: {
        // Banded gas giant with a storm spot
        const r = Math.floor(half) - 2;
        ctx.fillStyle = '#5f9fd8';
        ctx.beginPath();
        ctx.arc(cx, cy, r, 0, Math.PI * 2);
        ctx.fill();
        ctx.save();
        ctx.beginPath();
        ctx.arc(cx, cy, r, 0, Math.PI * 2);
        ctx.clip();
        ctx.fillStyle = '#3a6ea8';
        ctx.fillRect(cx - r, cy - Math.floor(r * 0.55), r * 2, 4);
        ctx.fillRect(cx - r, cy - 2, r * 2, 5);
        ctx.fillRect(cx - r, cy + Math.floor(r * 0.5), r * 2, 3);
        ctx.fillStyle = '#e8f0f8';
        ctx.beginPath();
        ctx.ellipse(cx - r * 0.3, cy + r * 0.28, 4, 2.5, 0, 0, Math.PI * 2);
        ctx.fill();
        ctx.restore();
        shadePlanet(ctx, cx, cy, r, '#2a4f7d');
        break;
      }
      case 3: {
        // Red planet with terrain patches and a polar cap
        const r = Math.floor(half) - 2;
        ctx.fillStyle = '#d4644a';
        ctx.beginPath();
        ctx.arc(cx, cy, r, 0, Math.PI * 2);
        ctx.fill();
        ctx.save();
        ctx.beginPath();
        ctx.arc(cx, cy, r, 0, Math.PI * 2);
        ctx.clip();
        ctx.fillStyle = '#a03a28';
        for (const spot of obstacle.spots) {
          ctx.beginPath();
          ctx.ellipse(
            cx + spot.dx * r,
            cy + spot.dy * r,
            Math.max(2, spot.r * r * 1.5),
            Math.max(1.5, spot.r * r),
            0,
            0,
            Math.PI * 2
          );
          ctx.fill();
        }
        ctx.fillStyle = '#f0ece0';
        ctx.beginPath();
        ctx.arc(cx, cy - r, r * 0.5, 0, Math.PI * 2);
        ctx.fill();
        ctx.restore();
        shadePlanet(ctx, cx, cy, r, '#7d3220');
        break;
      }
      default: {
        // UFO with blinking rim lights and a faint tractor beam
        const w = obstacle.width;
        const h = obstacle.height;
        const saucerY = Math.floor(obstacle.y + h * 0.4);
        const saucerRx = Math.floor(w / 2) - 1;
        const saucerRy = Math.max(4, Math.floor(h * 0.14));

        // Tractor beam fills the lower hitbox so the hit area reads fairly
        ctx.fillStyle = 'rgba(150, 220, 255, 0.22)';
        ctx.beginPath();
        ctx.moveTo(cx - 5, saucerY + saucerRy);
        ctx.lineTo(cx - Math.floor(w * 0.35), obstacle.y + h);
        ctx.lineTo(cx + Math.floor(w * 0.35), obstacle.y + h);
        ctx.lineTo(cx + 5, saucerY + saucerRy);
        ctx.closePath();
        ctx.fill();

        // Dome
        ctx.fillStyle = '#bfe4f4';
        ctx.beginPath();
        ctx.arc(cx, saucerY - 3, Math.floor(w * 0.22), Math.PI, Math.PI * 2);
        ctx.fill();

        // Saucer
        ctx.fillStyle = '#9aa2b4';
        ctx.beginPath();
        ctx.ellipse(cx, saucerY, saucerRx, saucerRy, 0, 0, Math.PI * 2);
        ctx.fill();
        ctx.fillStyle = '#6a7186';
        ctx.beginPath();
        ctx.ellipse(cx, saucerY + 2, saucerRx - 3, Math.max(2, saucerRy - 2), 0, 0, Math.PI);
        ctx.fill();

        // Blinking rim lights
        for (let i = 0; i < 3; i++) {
          const lx = cx + (i - 1) * Math.floor(w * 0.28);
          ctx.fillStyle = (Math.floor(frame / 12) + i) % 2 === 0 ? '#ffe066' : '#8a6a20';
          ctx.fillRect(lx - 1, saucerY - 1, 3, 3);
        }
        break;
      }
    }
  };

  const drawGroundObstacle = (ctx: CanvasRenderingContext2D, obstacle: Obstacle) => {
    const x = Math.floor(obstacle.x);
    const y = Math.floor(obstacle.y);
    const w = obstacle.width;
    const h = Math.floor(obstacle.height);
    const frame = frameCountRef.current;
    const j = obstacle.jitter;

    switch (obstacle.variant) {
      case 0: {
        // Asteroid rock spire: three stacked chunks with lit/shaded edges
        const baseH = Math.floor(h * 0.42);
        const midH = Math.floor(h * 0.34);
        const topH = h - baseH - midH;
        const baseY = y + h - baseH;
        const midY = baseY - midH;
        const midX = Math.floor(x + 1 + j[0] * 3);
        const topX = Math.floor(x + 4 + j[1] * 4);

        ctx.fillStyle = '#8f939e';
        ctx.fillRect(x, baseY, w, baseH);
        ctx.fillRect(midX, midY, w - 5, midH);
        ctx.fillRect(topX, y, w - 10, topH + 1);

        // Left highlights
        ctx.fillStyle = '#c4c8d2';
        ctx.fillRect(x, baseY, 2, baseH);
        ctx.fillRect(midX, midY, 2, midH);
        ctx.fillRect(topX, y, 2, topH);

        // Right shading + pits
        ctx.fillStyle = '#585c68';
        ctx.fillRect(x + w - 2, baseY, 2, baseH);
        ctx.fillRect(midX + w - 7, midY, 2, midH);
        ctx.fillRect(topX + w - 12, y, 2, topH);
        ctx.fillRect(Math.floor(x + 5 + j[2] * 8), Math.floor(baseY + baseH / 2), 3, 3);
        ctx.fillRect(Math.floor(midX + 3 + j[3] * 6), Math.floor(midY + midH / 2), 2, 2);
        break;
      }
      case 1: {
        // Crystal shards jutting from the ground
        const baseY = y + h;
        const mid = x + w / 2;

        // Short left shard
        ctx.fillStyle = '#8656c4';
        ctx.beginPath();
        ctx.moveTo(x + 4, Math.floor(y + h * 0.45));
        ctx.lineTo(x, baseY);
        ctx.lineTo(x + 9, baseY);
        ctx.closePath();
        ctx.fill();

        // Short right shard
        ctx.fillStyle = '#9060d0';
        ctx.beginPath();
        ctx.moveTo(x + w - 4, Math.floor(y + h * 0.55));
        ctx.lineTo(x + w - 8, baseY);
        ctx.lineTo(x + w, baseY);
        ctx.closePath();
        ctx.fill();

        // Main shard
        ctx.fillStyle = '#a678e0';
        ctx.beginPath();
        ctx.moveTo(mid, y);
        ctx.lineTo(x + 3, baseY);
        ctx.lineTo(x + w - 3, baseY);
        ctx.closePath();
        ctx.fill();

        // Dark right facet
        ctx.fillStyle = '#6b46a8';
        ctx.beginPath();
        ctx.moveTo(mid, y);
        ctx.lineTo(x + w - 3, baseY);
        ctx.lineTo(mid, baseY);
        ctx.closePath();
        ctx.fill();

        // Highlight sliver down the left facet
        ctx.fillStyle = '#e0ccf8';
        ctx.beginPath();
        ctx.moveTo(mid - 1, y + 3);
        ctx.lineTo(mid - 4, baseY);
        ctx.lineTo(mid - 2, baseY);
        ctx.closePath();
        ctx.fill();
        break;
      }
      default: {
        // Crashed satellite: mast + body + solar panels + blinking beacon
        const cx = Math.floor(x + w / 2);
        const bodyH = 14;
        const bodyW = 12;
        const bodyY = y + h - bodyH;

        // Mast and crossbar
        ctx.fillStyle = '#8f939e';
        ctx.fillRect(cx - 1, y + 4, 2, h - bodyH - 4);
        ctx.fillRect(cx - 5, Math.floor(y + h * 0.35), 10, 2);

        // Body
        ctx.fillStyle = '#aab0bc';
        ctx.fillRect(cx - bodyW / 2, bodyY, bodyW, bodyH);
        ctx.fillStyle = '#6a7186';
        ctx.fillRect(cx - bodyW / 2, bodyY + bodyH - 3, bodyW, 3);

        // Solar panels
        ctx.fillStyle = '#3a62c4';
        ctx.fillRect(x, bodyY + 3, 4, 8);
        ctx.fillRect(x + w - 4, bodyY + 3, 4, 8);
        ctx.fillStyle = '#7a9ae8';
        ctx.fillRect(x + 1, bodyY + 6, 2, 1);
        ctx.fillRect(x + w - 3, bodyY + 6, 2, 1);

        // Blinking beacon on top
        ctx.fillStyle = frame % 40 < 20 ? '#ff5a5a' : '#701c1c';
        ctx.fillRect(cx - 2, y, 4, 4);
        break;
      }
    }
  };

  const drawObstacle = (ctx: CanvasRenderingContext2D, obstacle: Obstacle) => {
    if (obstacle.type === 'planet') {
      drawFlyingObstacle(ctx, obstacle);
    } else {
      drawGroundObstacle(ctx, obstacle);
    }
  };

  const drawScore = (ctx: CanvasRenderingContext2D) => {
    ctx.fillStyle = '#ffffff';
    ctx.font = '20px "sysfont", "Courier New", monospace';
    ctx.textAlign = 'left';
    ctx.fillText(`Score: ${scoreRef.current}`, 20, 30);
    ctx.fillText(`High Score: ${highScore}`, 20, 55);
  };

  // Game logic
  const updatePlayer = () => {
    const player = playerRef.current;
    const groundY = groundYRef.current;

    // Apply gravity
    if (player.y < groundY - PLAYER_HEIGHT || player.velocityY < 0) {
      player.velocityY += GRAVITY;
      player.y += player.velocityY;
      player.isJumping = true;
    }

    // Ground collision
    if (player.y >= groundY - PLAYER_HEIGHT) {
      player.y = groundY - PLAYER_HEIGHT;
      player.velocityY = 0;
      player.isJumping = false;
    }

    // Update animations
    if (gameStateRef.current === 'idle') {
      // Animate idle state
      player.animationCounter++;
    } else if (!player.isJumping && gameStateRef.current === 'playing') {
      // Update run animation
      player.animationCounter++;
      if (player.animationCounter >= 5) {
        player.animationCounter = 0;
        player.frameIndex = (player.frameIndex + 1) % RUN_FRAMES.length;
      }
    }
  };

  const updateObstacles = () => {
    // Move obstacles
    obstaclesRef.current = obstaclesRef.current.filter(obstacle => {
      obstacle.x -= gameSpeedRef.current;
      return obstacle.x + obstacle.width > 0;
    });

    // Add new obstacles
    if (frameCountRef.current % 120 === 0) {
      obstaclesRef.current.push(createObstacle());
    }
  };

  const updateStars = () => {
    starsRef.current.forEach(star => {
      star.x -= star.speed;
      if (star.x < 0) {
        star.x = canvasWidthRef.current;
        star.y = Math.random() * (groundYRef.current - 20); // Keep stars above ground
      }
    });
  };

  const checkCollision = (): boolean => {
    const player = playerRef.current;
    const playerHitbox = {
      x: player.x + 10,
      y: player.y + 10,
      width: PLAYER_WIDTH - 20,
      height: PLAYER_HEIGHT - 20
    };

    for (const obstacle of obstaclesRef.current) {
      if (
        playerHitbox.x < obstacle.x + obstacle.width &&
        playerHitbox.x + playerHitbox.width > obstacle.x &&
        playerHitbox.y < obstacle.y + obstacle.height &&
        playerHitbox.y + playerHitbox.height > obstacle.y
      ) {
        return true;
      }
    }
    return false;
  };

  const jump = () => {
    const player = playerRef.current;
    if (!player.isJumping && gameStateRef.current === 'playing') {
      player.velocityY = JUMP_STRENGTH;
      player.isJumping = true;
    }
  };

  const startGame = () => {
    gameStateRef.current = 'playing';
    setGameState('playing');
    const groundY = groundYRef.current;
    playerRef.current = {
      x: 80,
      y: groundY - PLAYER_HEIGHT,
      velocityY: 0,
      isJumping: false,
      frameIndex: 0,
      animationCounter: 0
    };
    obstaclesRef.current = [];
    scoreRef.current = 0;
    setScore(0);
    // Scale game speed based on canvas width (base speed is 5 for 600px width)
    gameSpeedRef.current = (canvasWidthRef.current / 600) * 5;
    frameCountRef.current = 0;
  };

  const createConfetti = () => {
    const confetti: Confetti[] = [];
    const colors = ['#FF0000', '#00FF00', '#0000FF', '#FFFF00', '#FF00FF', '#00FFFF', '#FFA500'];

    for (let i = 0; i < 50; i++) {
      confetti.push({
        x: Math.random() * canvasWidthRef.current,
        y: -20 - Math.random() * 100,
        vx: (Math.random() - 0.5) * 4,
        vy: Math.random() * 2 + 2,
        color: colors[Math.floor(Math.random() * colors.length)],
        size: Math.floor(Math.random() * 3 + 3), // Pixelated sizes: 3, 4, 5
        shape: Math.random() > 0.5 ? 'square' : 'circle'
      });
    }
    return confetti;
  };

  const updateConfetti = () => {
    confettiRef.current = confettiRef.current.map(c => ({
      ...c,
      x: c.x + c.vx,
      y: c.y + c.vy,
      vy: c.vy + 0.1 // Gravity
    })).filter(c => c.y < canvasHeightRef.current + 20);

    // Add new confetti from top
    if (Math.random() < 0.3) {
      confettiRef.current.push(...createConfetti().slice(0, 5));
    }
  };

  const drawConfetti = (ctx: CanvasRenderingContext2D) => {
    confettiRef.current.forEach(c => {
      ctx.fillStyle = c.color;
      if (c.shape === 'square') {
        ctx.fillRect(Math.floor(c.x), Math.floor(c.y), c.size, c.size);
      } else {
        ctx.beginPath();
        ctx.arc(Math.floor(c.x), Math.floor(c.y), c.size / 2, 0, Math.PI * 2);
        ctx.fill();
      }
    });
  };

  const gameOver = async () => {
    gameStateRef.current = 'gameover';
    setGameState('gameover');

    if (scoreRef.current > highScore) {
      setHighScore(scoreRef.current);
      localStorage.setItem('orbitRunHighScore', scoreRef.current.toString());
    }

    await fetchLeaderboard();

    if (currentUser && userHandle) {
      await saveScore();
    }

    // Check if player made top 10
    const currentLeaderboard = leaderboard;
    const playerRank = currentLeaderboard.findIndex(entry => entry.handle === userHandle && entry.score === scoreRef.current);

    if (playerRank !== -1 && playerRank < 10) {
      setMadeTop10(true);
      setShowCelebration(true);
      confettiRef.current = createConfetti();
      celebrationStartTimeRef.current = Date.now();
    }
  };

  // Game loop
  const gameLoop = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Clear canvas
    ctx.clearRect(0, 0, canvasWidthRef.current, canvasHeightRef.current);

    // Draw background
    drawBackground(ctx);

    if (gameStateRef.current === 'idle') {
      // Update player animation during idle
      updatePlayer();

      drawPlayer(ctx);
      ctx.fillStyle = '#ffffff';
      ctx.font = '24px "sysfont", "Courier New", monospace';
      ctx.textAlign = 'center';
      const startText = isMobile ? 'Tap to start' : 'Click or press SPACE to start';
      ctx.fillText(startText, canvasWidthRef.current / 2, canvasHeightRef.current / 2);
      ctx.textAlign = 'left';
    } else if (gameStateRef.current === 'playing') {
      frameCountRef.current++;

      // Update stars
      updateStars();

      // Update player
      updatePlayer();

      // Update obstacles
      updateObstacles();

      // Draw everything
      drawPlayer(ctx);
      obstaclesRef.current.forEach(obstacle => drawObstacle(ctx, obstacle));
      drawScore(ctx);

      // Update score
      scoreRef.current = Math.floor(frameCountRef.current / 10);
      setScore(scoreRef.current);

      // Increase difficulty (scale speed increase based on canvas width)
      if (frameCountRef.current % 300 === 0) {
        gameSpeedRef.current += (canvasWidthRef.current / 600) * 0.5;
      }

      // Check collision
      if (checkCollision()) {
        gameOver();
        return;
      }
    } else if (gameStateRef.current === 'gameover') {
      // Keep drawing final frame
      drawPlayer(ctx);
      obstaclesRef.current.forEach(obstacle => drawObstacle(ctx, obstacle));
      drawScore(ctx);

      // Draw celebration confetti if player made top 10
      if (showCelebration) {
        const elapsed = Date.now() - celebrationStartTimeRef.current;

        if (elapsed < 10000) { // 10 seconds
          updateConfetti();
          drawConfetti(ctx);
        } else {
          setShowCelebration(false);
          confettiRef.current = [];
        }
      }
    }

    animationFrameRef.current = requestAnimationFrame(gameLoop);
  };

  // Initialize game loop
  useEffect(() => {
    const savedHighScore = localStorage.getItem('orbitRunHighScore');
    if (savedHighScore) {
      setHighScore(parseInt(savedHighScore));
    }

    gameLoop();

    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, []);

  // Handle input
  useEffect(() => {
    const handleInput = (e: KeyboardEvent | MouseEvent | TouchEvent) => {
      if (e instanceof KeyboardEvent && e.code !== 'Space') return;

      e.preventDefault();

      if (gameStateRef.current === 'idle') {
        startGame();
      } else if (gameStateRef.current === 'playing') {
        jump();
      }
    };

    window.addEventListener('keydown', handleInput);
    const canvas = canvasRef.current;
    if (canvas) {
      canvas.addEventListener('click', handleInput as any);
      canvas.addEventListener('touchstart', handleInput as any);
    }

    return () => {
      window.removeEventListener('keydown', handleInput);
      if (canvas) {
        canvas.removeEventListener('click', handleInput as any);
        canvas.removeEventListener('touchstart', handleInput as any);
      }
    };
  }, []);

  // Handle restart from button
  const handleRestart = () => {
    // Clear any existing animation frame
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
    }
    startGame();
    // Restart game loop
    gameLoop();
  };

  const [showLeaderboard, setShowLeaderboard] = useState(false);

  const toggleLeaderboard = () => {
    if (!showLeaderboard) {
      fetchLeaderboard();
    }
    setShowLeaderboard(!showLeaderboard);
  };

  const handleModalClose = () => {
    setShowLeaderboard(false);

    // Reset game to idle state when closing from game over (same pattern as handleRestart)
    if (gameStateRef.current === 'gameover') {
      // Clear any existing animation frame
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }

      // Reset all game state to idle
      gameStateRef.current = 'idle';
      setGameState('idle');
      setShowCelebration(false);
      setMadeTop10(false);
      confettiRef.current = [];

      // Reset player to ground position
      const groundY = groundYRef.current;
      playerRef.current = {
        x: 80,
        y: groundY - PLAYER_HEIGHT,
        velocityY: 0,
        isJumping: false,
        frameIndex: 0,
        animationCounter: 0
      };

      // Clear all obstacles
      obstaclesRef.current = [];
      scoreRef.current = 0;
      setScore(0);
      frameCountRef.current = 0;
      gameSpeedRef.current = (canvasWidthRef.current / 600) * 5;

      // Restart game loop (just like handleRestart does)
      gameLoop();
    }
  };

  return (
    <div className="orbit-run">
      {gameState !== 'gameover' && (
        <button onClick={toggleLeaderboard} className="view-leaderboard-btn">
          View Leaderboard
        </button>
      )}

      <canvas
        ref={canvasRef}
        width={CANVAS_WIDTH}
        height={CANVAS_HEIGHT}
        className="orbit-run-canvas"
      />

      {gameState === 'gameover' && (
        <>
          {isMobile ? (
            <div className="game-over-modal-overlay" onClick={handleModalClose}>
              <div className="game-over-modal-content" onClick={(e) => e.stopPropagation()}>
                <button className="modal-close-x" onClick={handleModalClose}>×</button>
                <h2>Game Over!</h2>
                <p className="final-score">Score: {score}</p>
                <p className="high-score">High Score: {highScore}</p>

                {!currentUser && (
                  <div className="auth-prompt">
                    <p>Login to save your score!</p>
                    <button onClick={onOpenLoginSignup} className="login-btn">
                      Login / Sign Up
                    </button>
                  </div>
                )}

                {currentUser && userHandle && (
                  <div className="score-saved">
                    <p>Score saved as @{userHandle}!</p>
                  </div>
                )}

                <div className="leaderboard">
                  <h3>Top 10 Scores</h3>
                  {leaderboard.length === 0 ? (
                    <p className="no-scores">No scores yet. Be the first!</p>
                  ) : (
                    <ol>
                      {leaderboard.map((entry, index) => (
                        <li key={index} className={entry.handle === userHandle ? 'current-user' : ''}>
                          <span className="rank">#{index + 1}</span>
                          <span className="handle">@{entry.handle}</span>
                          <span className="score">{entry.score}</span>
                        </li>
                      ))}
                    </ol>
                  )}
                </div>

                <button onClick={handleRestart} className="play-again-btn">
                  Play Again
                </button>
              </div>
            </div>
          ) : (
            <div className="game-over-panel">
              <button className="modal-close-x" onClick={handleModalClose}>×</button>
              <div className="game-over-content">
                <div className="game-over-left">
                  <h2>Game Over!</h2>
                  <p className="final-score">Score: {score}</p>
                  <p className="high-score">High Score: {highScore}</p>

                  {!currentUser && (
                    <div className="auth-prompt">
                      <p>Login to save your score!</p>
                      <button onClick={onOpenLoginSignup} className="login-btn">
                        Login / Sign Up
                      </button>
                    </div>
                  )}

                  {currentUser && userHandle && (
                    <div className="score-saved">
                      <p>Score saved as @{userHandle}!</p>
                    </div>
                  )}

                  <button onClick={handleRestart} className="play-again-btn">
                    Play Again
                  </button>
                </div>

                <div className="game-over-right">
                  <div className="leaderboard">
                    <h3>Top 10 Scores</h3>
                    {leaderboard.length === 0 ? (
                      <p className="no-scores">No scores yet. Be the first!</p>
                    ) : (
                      <ol>
                        {leaderboard.map((entry, index) => (
                          <li key={index} className={entry.handle === userHandle ? 'current-user' : ''}>
                            <span className="rank">#{index + 1}</span>
                            <span className="handle">@{entry.handle}</span>
                            <span className="score">{entry.score}</span>
                          </li>
                        ))}
                      </ol>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}
        </>
      )}

      {showLeaderboard && gameState !== 'gameover' && (
        <div className="leaderboard-modal-overlay" onClick={handleModalClose}>
          <div className="leaderboard-modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close-x" onClick={handleModalClose}>×</button>
            <div className="leaderboard">
              <h3>Top 10 Scores</h3>
              {leaderboard.length === 0 ? (
                <p className="no-scores">No scores yet. Be the first!</p>
              ) : (
                <ol>
                  {leaderboard.map((entry, index) => (
                    <li key={index} className={entry.handle === userHandle ? 'current-user' : ''}>
                      <span className="rank">#{index + 1}</span>
                      <span className="handle">@{entry.handle}</span>
                      <span className="score">{entry.score}</span>
                    </li>
                  ))}
                </ol>
              )}
            </div>
            <button onClick={handleModalClose} className="close-leaderboard-btn">
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default OrbitRun;
